import { HandCoins } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Banner } from '@/components/feedback/Banner'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import {
  useMarkPricingReady,
  useRequestPricing,
  useWithdrawPricing,
} from '@/features/quotes/api/useQuote'
import { PricingRequestSheet } from '@/features/quotes/components/PricingRequestSheet'
import { QuotePricingPill } from '@/features/quotes/components/QuotePricingPill'
import { reopenedLine } from '@/features/quotes/pricingReset'
import { pricingErrorMessage } from '@/features/quotes/quoteText'
import { useFormat } from '@/i18n/useFormat'
import { CAP, can } from '@/lib/can'
import type { CurrentUser, QuoteDetail } from '@/types/domain'

/* «Расчёт» — where the request for a price stands, and the three acts that move
 * it (S-2a, owner decisions O1, O5, O6).
 *
 * It replaced `PricingDeskCard`, which sent `reason: "pricing"` to the team
 * route and was mounted nowhere: the ask existed in code and could not be made
 * from the product, and nothing recorded that it had been. The state is now a
 * stored fact on the quote (`record.pricing`), served on every read, so this
 * card RENDERS it and never derives it — a reset by somebody's cargo edit
 * arrives here as `requested` without the card having seen the edit.
 *
 * A RESET is named as one (N-11): the server stores what the edit changed
 * (`pricing.reopenCauses`), and the card then reads «Сброшен после правки:
 * Инкотермс — <who>, <when>» in place of «Запрос: …», which would credit the
 * editor with a request they never made. The pill stays «Ждёт расчёта»: the
 * state IS requested, and the pill keys off the state alone.
 *
 * WHO SEES WHICH BUTTON is capability-shaped (`lib/can.ts`), never role-shaped:
 *  - «Запросить расчёт…» — `quote.team.assign`, on a draft. In any state: from
 *    requested or ready it is a re-request, possibly of somebody else;
 *  - «Расчёт готов» — `quote.pricing.provide`, on a draft that is not ready.
 *    From none too (O5): a pricer may price a quote nobody asked about;
 *  - «Отозвать запрос» — `quote.team.assign`, while requested.
 * A control a reader may never use is absent rather than greyed out (README
 * § Authorization). The server re-checks every one of them (AD-21): the card is
 * only shaped by these.
 *
 * Drafts only, at the page's choice: the acts all refuse a quote that is no
 * longer a draft, and on a sent quote the state is history rather than work.
 *
 * Refusals: the request's land in its sheet; «ready» and «withdraw» have no
 * sheet, so theirs render in a Banner at the top of this card — never a Toast,
 * which would leave with the answer to "why did nothing happen". Each press
 * clears the other's refusal so one Banner never speaks for the wrong button.
 */
export function QuotePricingCard({
  record,
  user,
}: {
  record: QuoteDetail
  /** The signed-in reader, for the capability tests — the page already holds
   *  the session, and undefined (still loading) shows no buttons. */
  user: CurrentUser | undefined
}) {
  const { t } = useTranslation()
  const format = useFormat()
  const [asking, setAsking] = useState(false)

  const request = useRequestPricing(record.id)
  const ready = useMarkPricingReady(record.id)
  const withdraw = useWithdrawPricing(record.id)

  const pricing = record.pricing
  const draft = record.status === 'draft'
  const mayAsk = draft && can(user, CAP.quoteTeamAssign)
  const mayMarkReady =
    draft && pricing.state !== 'ready' && can(user, CAP.quotePricingProvide)
  const mayWithdraw = draft && pricing.state === 'requested' && can(user, CAP.quoteTeamAssign)

  const actError = ready.error ?? withdraw.error

  /* Who and when, from the served names. A ready price that nobody asked for
     (O5) has no requester. A reset (N-11) carries its causes, and its
     requested_* are the editor's — so it reads «Сброшен …», not «Запрос …». */
  const facts: string[] = []
  if (pricing.assigneeName) {
    facts.push(t('quotes.pricingState.assignee', { name: pricing.assigneeName }))
  }
  if (pricing.state === 'ready' && pricing.readyAt) {
    facts.push(
      t('quotes.pricingState.readyBy', {
        name: pricing.readyByName ?? t('common.empty'),
        when: format.timestamp(pricing.readyAt),
      }),
    )
  }
  const reopened = reopenedLine(pricing, t, format)
  if (reopened) {
    facts.push(reopened)
  } else if (pricing.state !== 'none' && pricing.requestedAt) {
    facts.push(
      pricing.requestedByName
        ? t('quotes.pricingState.requestedBy', {
            name: pricing.requestedByName,
            when: format.timestamp(pricing.requestedAt),
          })
        : t('quotes.pricingState.requestedAt', {
            when: format.timestamp(pricing.requestedAt),
          }),
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <span className="flex items-center gap-2">
            <HandCoins size={15} className="text-text-muted" aria-hidden />
            {t('quotes.pricingState.title')}
          </span>
        </CardTitle>
      </CardHeader>

      <CardBody className="flex flex-col gap-3 pt-2">
        {actError ? (
          <Banner tone="negative" title={pricingErrorMessage(actError, t)} />
        ) : null}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-col gap-1.5">
            <span>
              <QuotePricingPill state={pricing.state} />
            </span>
            {facts.length > 0 ? (
              <ul className="text-text-muted flex flex-col gap-0.5 text-[12.5px] leading-snug font-light">
                {facts.map((fact) => (
                  <li key={fact}>{fact}</li>
                ))}
              </ul>
            ) : mayAsk ? (
              <p className="text-text-muted text-[12.5px] leading-snug font-light">
                {t('quotes.pricingState.noneHint')}
              </p>
            ) : null}
          </div>

          {mayAsk || mayMarkReady || mayWithdraw ? (
            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
              {mayWithdraw ? (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={withdraw.isPending}
                  onClick={() => {
                    ready.reset()
                    withdraw.mutate()
                  }}
                >
                  {withdraw.isPending
                    ? t('quotes.pricingState.actions.withdrawing')
                    : t('quotes.pricingState.actions.withdraw')}
                </Button>
              ) : null}
              {mayAsk ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    request.reset()
                    setAsking(true)
                  }}
                >
                  {pricing.state === 'none'
                    ? t('quotes.pricingState.actions.request')
                    : t('quotes.pricingState.actions.reRequest')}
                </Button>
              ) : null}
              {mayMarkReady ? (
                <Button
                  size="sm"
                  disabled={ready.isPending}
                  onClick={() => {
                    withdraw.reset()
                    ready.mutate()
                  }}
                >
                  {ready.isPending
                    ? t('quotes.pricingState.actions.marking')
                    : t('quotes.pricingState.actions.ready')}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </CardBody>

      <PricingRequestSheet
        open={asking}
        onClose={() => setAsking(false)}
        record={record}
        pending={request.isPending}
        error={request.error ?? undefined}
        onConfirm={(variables) =>
          // Closed only on success: a refusal keeps the choice and the note
          // in front of the reader with the reason beside them.
          request.mutate(variables, { onSuccess: () => setAsking(false) })
        }
      />
    </Card>
  )
}
